/**
 * Social sign-in, as the browser sees it.
 *
 * `docs/SOCIAL_LOGIN.md` is the binding document. The distinction it opens with governs this
 * whole directory: **signing in is not a data connection.** Nothing here touches
 * `/connections/*`, nothing here asks for a data scope, and nothing here ever receives a
 * provider token — the server reads the identity and discards the token in the same breath.
 *
 * The flow, and the four routes it needs:
 *
 * ```
 * GET  /api/v1/auth/oauth/providers                → which providers this server can offer
 * GET  /api/v1/auth/oauth/:provider/start?returnTo= → a *browser navigation*, not a fetch
 * GET  /api/v1/auth/oauth/:provider/callback        → the server's business; it redirects back here
 * POST /api/v1/auth/oauth/:provider/email           → Facebook returned no address; here is one
 * ```
 *
 * The callback is the only unauthenticated hop, so everything it hands back through the query
 * string is treated as hostile: `returnTo` is checked against `safeReturnTo` before any
 * navigation, and an unknown error code falls back to copy that still says what to do next.
 */

import type { PublicBranding } from '@anubis-connector/shared'

import { API_BASE } from '../../../lib/api'

/* ── the two providers ────────────────────────────────────────────────────── */

export const SOCIAL_PROVIDERS = ['google', 'facebook'] as const

export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number]

/** Network names are never translated — see `docs/I18N.md` § What is not translated. */
export const SOCIAL_LABEL: Record<SocialProvider, string> = {
  google: 'Google',
  facebook: 'Facebook',
}

export const isSocialProvider = (value: string | null | undefined): value is SocialProvider =>
  value === 'google' || value === 'facebook'

/* ── where the browser is sent ────────────────────────────────────────────── */

/**
 * The default landing place after a sign-in that came back clean.
 *
 * Also the floor `safeReturnTo` falls back to, which is why it lives here rather than in each
 * screen: a tampered `returnTo` must never leave the caller without a destination.
 */
export const DEFAULT_DESTINATION = '/app/dashboard'

/**
 * Accepts only an app-relative path, and only one that cannot leave this origin.
 *
 * `//evil.example` and `/\evil.example` are protocol-relative URLs that `history.pushState`
 * resolves off-origin, so both are rejected alongside anything carrying a scheme. This is the
 * same rule the connect flow's server-side `returnTo` validator applies, kept on the client
 * because the value arrives from a redirect nobody authenticated.
 */
export const safeReturnTo = (raw: string | null | undefined, fallback = DEFAULT_DESTINATION): string => {
  if (!raw) return fallback
  const value = raw.trim()
  if (!value.startsWith('/')) return fallback
  if (value.startsWith('//') || value.startsWith('/\\')) return fallback
  if (value.includes('\\')) return fallback
  return value
}

/**
 * The URL for `Continue with Google`.
 *
 * This is assigned to `window.location`, never fetched: the response is a 302 to Google, and a
 * `fetch` would follow it into a CORS wall instead of showing the consent screen.
 */
export const socialStartUrl = (provider: SocialProvider, returnTo?: string): string => {
  const search = new URLSearchParams()
  const destination = safeReturnTo(returnTo)
  search.set('returnTo', destination)
  return `${API_BASE}/api/v1/auth/oauth/${provider}/start?${search.toString()}`
}

/* ── GET /api/v1/auth/oauth/providers ─────────────────────────────────────── */

/**
 * Whether an app is registered for a provider, read the way the connect surface reads it.
 *
 * `frontend/src/features/connections/types.ts` already had to cope with three spellings of the
 * same fact across the registry, the docs and the adapter seam. The same tolerance applies here
 * for the same reason — and, as there, **absence means unavailable**: a button that dead-ends at
 * a provider with no client id is worse than no button.
 */
export interface SocialProviderWire {
  provider?: string
  id?: string
  label?: string
  configured?: boolean
  configurable?: boolean
  isConfigured?: boolean
  enabled?: boolean
}

export const readSocialConfigured = (wire: SocialProviderWire): boolean => {
  if (wire.enabled === false) return false
  if (typeof wire.configured === 'boolean') return wire.configured
  if (typeof wire.configurable === 'boolean') return wire.configurable
  if (typeof wire.isConfigured === 'boolean') return wire.isConfigured
  return false
}

/** The providers a caller may actually offer, in the fixed display order Google → Facebook. */
export const readAvailableProviders = (wire: readonly SocialProviderWire[]): SocialProvider[] => {
  const available = new Set<SocialProvider>()
  for (const row of wire) {
    const id = row.provider ?? row.id
    if (isSocialProvider(id) && readSocialConfigured(row)) available.add(id)
  }
  return SOCIAL_PROVIDERS.filter((provider) => available.has(provider))
}

/* ── POST /api/v1/auth/oauth/:provider/email ──────────────────────────────── */

/** What the server answers when an address is supplied for an identity that had none. */
export interface OAuthEmailResult {
  /** `sent` means a verification mail is on its way and nothing exists yet. */
  status: 'sent' | 'verified'
  email: string
}

export interface OAuthEmailWire {
  status?: string
  email?: string
  ok?: boolean
  sent?: boolean
}

export const readOAuthEmailResult = (wire: OAuthEmailWire, fallbackEmail: string): OAuthEmailResult => ({
  status: wire.status === 'verified' ? 'verified' : 'sent',
  email: wire.email ?? fallbackEmail,
})

/* ── the return journey ───────────────────────────────────────────────────── */

/**
 * The outcomes a sign-in card has to be able to render.
 *
 * `completing` is not an error: the callback set the refresh cookie and bounced the browser
 * back, and the session is one `POST /auth/refresh` away. It exists so that hop has a screen of
 * its own instead of a blank card.
 */
export type SocialReturn =
  | { kind: 'completing'; provider: SocialProvider | null; returnTo: string }
  | { kind: 'emailInUse'; provider: SocialProvider | null; email: string | null }
  | { kind: 'emailMissing'; provider: SocialProvider; state: string | null }
  | { kind: 'failed'; provider: SocialProvider | null; code: string }

/** Every spelling of "which provider" the callback might use. */
const readProvider = (params: URLSearchParams): SocialProvider | null => {
  for (const key of ['provider', 'authProvider', 'oauthProvider', 'social']) {
    const value = params.get(key)
    if (isSocialProvider(value)) return value
  }
  return null
}

const readFirst = (params: URLSearchParams, keys: readonly string[]): string | null => {
  for (const key of keys) {
    const value = params.get(key)
    if (value !== null && value !== '') return value
  }
  return null
}

const ERROR_KEYS = ['authError', 'oauthError', 'oauth_error', 'error'] as const
const STATE_KEYS = ['state', 'authState', 'oauthState'] as const
const RETURN_KEYS = ['returnTo', 'next'] as const
const SUCCESS_KEYS = ['signedIn', 'authOk', 'oauth'] as const

/** Truthy in the loose way a redirect writes booleans: `1`, `true`, `ok`, or a bare key. */
const isTruthy = (value: string | null): boolean =>
  value !== null && value !== '0' && value !== 'false' && value !== 'no'

/**
 * Reads the callback's answer off the query string.
 *
 * Returns `null` for an ordinary visit, so the sign-in card renders its form as usual.
 */
export const readSocialReturn = (params: URLSearchParams): SocialReturn | null => {
  const provider = readProvider(params)
  const rawCode = readFirst(params, ERROR_KEYS)

  if (rawCode !== null) {
    const code = normaliseSocialErrorCode(rawCode)
    if (code === 'OAUTH_EMAIL_IN_USE') {
      return { kind: 'emailInUse', provider, email: params.get('email') }
    }
    if (code === 'OAUTH_EMAIL_MISSING') {
      // Only Facebook can reach this: Google's `email` scope is not optional to it.
      return {
        kind: 'emailMissing',
        provider: provider ?? 'facebook',
        state: readFirst(params, STATE_KEYS),
      }
    }
    return { kind: 'failed', provider, code }
  }

  const success = SUCCESS_KEYS.some((key) => params.has(key) && isTruthy(params.get(key)))
  if (success) {
    return { kind: 'completing', provider, returnTo: safeReturnTo(readFirst(params, RETURN_KEYS)) }
  }

  return null
}

/** The query keys a return screen must strip once the person has moved on from it. */
export const SOCIAL_RETURN_KEYS: readonly string[] = [
  ...ERROR_KEYS,
  ...STATE_KEYS,
  ...SUCCESS_KEYS,
  'provider',
  'authProvider',
  'oauthProvider',
  'social',
  'returnTo',
  'next',
  'error_description',
]

/* ── error codes ──────────────────────────────────────────────────────────── */

/**
 * The stable codes this UI has copy for.
 *
 * `docs/I18N.md`: the UI keys off `code` and falls back to the server's `message` when a code is
 * unknown. Aliases are folded in here so one screen's vocabulary does not depend on which of
 * several reasonable names the server picked — and so the provider's own `access_denied`,
 * which arrives verbatim from Google, lands on the right screen.
 */
export type SocialErrorCode =
  | 'OAUTH_EMAIL_IN_USE'
  | 'OAUTH_EMAIL_MISSING'
  | 'OAUTH_DENIED'
  | 'OAUTH_STATE_INVALID'
  | 'OAUTH_EXCHANGE_FAILED'
  | 'OAUTH_NOT_CONFIGURED'
  | 'OAUTH_DISABLED'
  | 'ACCOUNT_SUSPENDED'
  | 'RATE_LIMITED'
  | 'OAUTH_FAILED'

const ERROR_ALIASES: Record<string, SocialErrorCode> = {
  OAUTH_EMAIL_IN_USE: 'OAUTH_EMAIL_IN_USE',
  EMAIL_IN_USE: 'OAUTH_EMAIL_IN_USE',
  EMAIL_ALREADY_REGISTERED: 'OAUTH_EMAIL_IN_USE',
  EMAIL_ALREADY_IN_USE: 'OAUTH_EMAIL_IN_USE',
  ACCOUNT_EXISTS: 'OAUTH_EMAIL_IN_USE',
  IDENTITY_EMAIL_CONFLICT: 'OAUTH_EMAIL_IN_USE',

  OAUTH_EMAIL_MISSING: 'OAUTH_EMAIL_MISSING',
  EMAIL_MISSING: 'OAUTH_EMAIL_MISSING',
  EMAIL_REQUIRED: 'OAUTH_EMAIL_MISSING',
  NO_EMAIL: 'OAUTH_EMAIL_MISSING',
  PROVIDER_EMAIL_MISSING: 'OAUTH_EMAIL_MISSING',

  OAUTH_DENIED: 'OAUTH_DENIED',
  ACCESS_DENIED: 'OAUTH_DENIED',
  USER_CANCELLED: 'OAUTH_DENIED',
  USER_DENIED: 'OAUTH_DENIED',
  CONSENT_REQUIRED: 'OAUTH_DENIED',

  OAUTH_STATE_INVALID: 'OAUTH_STATE_INVALID',
  INVALID_STATE: 'OAUTH_STATE_INVALID',
  STATE_EXPIRED: 'OAUTH_STATE_INVALID',
  STATE_NOT_FOUND: 'OAUTH_STATE_INVALID',
  STATE_ALREADY_USED: 'OAUTH_STATE_INVALID',

  OAUTH_EXCHANGE_FAILED: 'OAUTH_EXCHANGE_FAILED',
  TOKEN_EXCHANGE_FAILED: 'OAUTH_EXCHANGE_FAILED',
  PROVIDER_ERROR: 'OAUTH_EXCHANGE_FAILED',
  UPSTREAM_ERROR: 'OAUTH_EXCHANGE_FAILED',
  SERVER_ERROR: 'OAUTH_EXCHANGE_FAILED',

  OAUTH_NOT_CONFIGURED: 'OAUTH_NOT_CONFIGURED',
  PROVIDER_NOT_CONFIGURED: 'OAUTH_NOT_CONFIGURED',
  NOT_CONFIGURED: 'OAUTH_NOT_CONFIGURED',

  OAUTH_DISABLED: 'OAUTH_DISABLED',
  SOCIAL_SIGNIN_DISABLED: 'OAUTH_DISABLED',
  PROVIDER_DISABLED: 'OAUTH_DISABLED',

  ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
  USER_SUSPENDED: 'ACCOUNT_SUSPENDED',
  ORG_SUSPENDED: 'ACCOUNT_SUSPENDED',
  SUSPENDED: 'ACCOUNT_SUSPENDED',

  RATE_LIMITED: 'RATE_LIMITED',
  TOO_MANY_REQUESTS: 'RATE_LIMITED',
}

/** Uppercases, swaps separators, then folds the alias table. Unknown codes stay themselves. */
export const normaliseSocialErrorCode = (raw: string): string => {
  const key = raw.trim().toUpperCase().replace(/[\s-]+/g, '_')
  return ERROR_ALIASES[key] ?? key
}

const KNOWN_CODES = new Set<string>(Object.values(ERROR_ALIASES))

export const isKnownSocialErrorCode = (code: string): code is SocialErrorCode =>
  KNOWN_CODES.has(code) || code === 'OAUTH_FAILED'

/* ── password accounts that have no password ──────────────────────────────── */

/**
 * The codes `POST /auth/login` answers with when the account has no `passwordHash`.
 *
 * `User.passwordHash` is nullable precisely so an account created through Google is not given an
 * invented one, which makes this a normal outcome of the password form rather than a failure —
 * and the copy names the provider that account actually uses.
 */
const PASSWORDLESS_CODES = new Set([
  'PASSWORD_NOT_SET',
  'NO_PASSWORD_SET',
  'ACCOUNT_USES_SOCIAL_LOGIN',
  'ACCOUNT_HAS_NO_PASSWORD',
  'SOCIAL_ACCOUNT',
  'USE_SOCIAL_LOGIN',
])

export const isPasswordlessAccountCode = (code: string): boolean =>
  PASSWORDLESS_CODES.has(normaliseSocialErrorCode(code))

/** The provider such an error names, when it names one. */
export const readErrorProvider = (details: Record<string, unknown> | undefined): SocialProvider | null => {
  if (!details) return null
  const value = details['provider']
  return typeof value === 'string' && isSocialProvider(value) ? value : null
}

/* ── the white-label switch ───────────────────────────────────────────────── */

/**
 * Whether this tenant shows social sign-in at all.
 *
 * `docs/SOCIAL_LOGIN.md` § Interface: the provider buttons cannot be restyled to a partner
 * palette, because both companies enforce their own mark and wording. The honest control is
 * therefore a switch, not a theme — and this reads it.
 *
 * The field is optional on the wire: a server that predates the switch, or a tenant that never
 * touched it, means **on**. Only an explicit `false` (or an explicit `hideSocialSignIn: true`)
 * turns the buttons off, so the feature never disappears because of a shape mismatch.
 */
const readBooleanField = (source: object, key: string): boolean | undefined => {
  const value = (source as Record<string, unknown>)[key]
  return typeof value === 'boolean' ? value : undefined
}

export const socialSignInEnabled = (branding: PublicBranding): boolean => {
  for (const key of ['socialSignInEnabled', 'socialSignIn', 'socialLoginEnabled']) {
    const value = readBooleanField(branding, key)
    if (value !== undefined) return value
  }
  for (const key of ['hideSocialSignIn', 'socialSignInDisabled']) {
    const value = readBooleanField(branding, key)
    if (value !== undefined) return !value
  }
  return true
}
